import React, { useEffect, useState } from 'react';
import { Alert, Badge, Box, Button, Fab, Paper, Snackbar, Stack, Step, StepLabel, Stepper, Tab, Tabs } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ArrowBack, ArrowForward, HistoryOutlined as HistoryIcon, PointOfSaleOutlined as SaleIcon, ShoppingCartOutlined as CartIcon } from '@mui/icons-material';
import { ShoppingCartProvider, useShoppingCart } from '../contexts/ShoppingCartContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Role, Sale } from '../types';
import { SalesShoppingPanel, SalesHistoryPanel } from '../features/sales';
import { CustomerSelector } from '../components/sales/CustomerSelector';
import { ItemSelector } from '../components/sales/ItemSelector';
import { ShoppingCart } from '../components/sales/ShoppingCart';
import SaleSummaryDialog from '../components/sales/SaleSummaryDialog';
import PageHeader from '../components/common/PageHeader';
import HelpTooltip from '../components/common/HelpTooltip';
import { useMobileTopBarAction } from '../components/common/MobileTopBarActionsContext';
import { useIsMobileShell } from '../components/common/useIsMobileShell';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`sales-tabpanel-${index}`}
    aria-labelledby={`sales-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ pt: { xs: 0, md: 3 } }}>{children}</Box>}
  </div>
);

const SalesPageContent: React.FC = () => {
  const { state, getCartItemCount, resetSale } = useShoppingCart();
  const { t } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobileShell();
  const readOnly = user?.role === Role.SUPER_ADMIN;
  const [activeTab, setActiveTab] = useState(() => readOnly || location.state?.salesTab === 1 || location.state?.showSalesHistory ? 1 : 0);
  const [mobileSaleStep, setMobileSaleStep] = useState(0);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [completedSale, setCompletedSale] = useState<Sale | null>(null);
  const [shoppingPanelKey, setShoppingPanelKey] = useState(0);
  const companyId = new URLSearchParams(location.search).get('companyId') || undefined;
  const companyScopeQuery = useQuery({
    queryKey: ['sales', 'company-scope', companyId, user?.role, user?.companyId],
    queryFn: () => user?.role === Role.SUPER_ADMIN
      ? apiClient.getCompany(companyId!)
      : apiClient.getCurrentCompany(),
    enabled: Boolean(companyId && user),
  });

  useEffect(() => {
    if (readOnly && activeTab !== 1) {
      setActiveTab(1);
      return;
    }
    if (location.state?.salesTab === activeTab) return;
    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: { ...location.state, salesTab: activeTab },
    });
  }, [activeTab, location.hash, location.pathname, location.search, location.state, navigate, readOnly]);

  const cartItemCount = getCartItemCount();
  const mobileStepLabels = [t('sales.stepCustomer'), t('sales.stepItems'), t('sales.stepCart')];

  useMobileTopBarAction(isMobile && !readOnly ? {
    id: 'sales-toggle-history',
    label: t(activeTab === 0 ? 'sales.salesHistory' : 'sales.shopping'),
    icon: activeTab === 0 ? <HistoryIcon fontSize="small" /> : <SaleIcon fontSize="small" />,
    order: 20,
    onSelect: () => setActiveTab(current => current === 0 ? 1 : 0),
  } : null);

  const handleSaleCompleted = (sale: Sale) => {
    setCompletedSale(sale);
    setShowSuccessMessage(true);
    setActiveTab(0);
  };

  const handleSaleSummaryClose = () => {
    setCompletedSale(null);
    resetSale();
    setMobileSaleStep(0);
    // CustomerSelector and ItemSelector keep their own search/selection state.
    // Remount the shopping panel so the next sale starts with a clean screen.
    setShoppingPanelKey((currentKey) => currentKey + 1);
  };

  return (
    <Box sx={{ flexGrow: 1, px: isMobile && activeTab === 0 ? 2.5 : 0, pt: isMobile && activeTab === 0 ? 3 : 0 }}>
      <PageHeader
        title={t('nav.sales')}
        icon={<SaleIcon />}
        accent="sales"
        titleAccessory={<HelpTooltip helpId="sales-page-help" title={t('help.sales.title')} content={t('help.sales.content')} maxWidth={600} placement="bottom-start" />}
        description={t('sales.pageDescription')}
        mobileMode="hidden"
      />
      {companyId && <Alert severity="info" sx={{ mb: 2 }}>
        {t('sales.companyScope')}: <strong>{companyScopeQuery.data?.name || companyId}</strong>
      </Alert>}

      {!isMobile && !readOnly && <Paper variant="outlined" sx={{ width: '100%', borderRadius: 2, overflow: 'hidden', background: theme => `linear-gradient(120deg, ${theme.palette.background.paper}, ${theme.palette.action.hover})` }}>
        <Tabs
          value={activeTab}
          onChange={(_event, value) => setActiveTab(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{ minHeight: 58, '& .MuiTab-root': { minHeight: 58, fontWeight: 600, gap: 1 }, '& .MuiTab-iconWrapper': { mb: 0 } }}
        >
          <Tab icon={<SaleIcon />} iconPosition="start" label={t('sales.shopping')} id="sales-tab-0" aria-controls="sales-tabpanel-0" />
          <Tab icon={<HistoryIcon />} iconPosition="start" label={t('sales.salesHistory')} id="sales-tab-1" aria-controls="sales-tabpanel-1" />
        </Tabs>
      </Paper>}

      {!readOnly && <TabPanel value={activeTab} index={0}>
        {isMobile ? (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Stepper activeStep={mobileSaleStep} alternativeLabel sx={{
                '& .MuiStepLabel-label': { mt: .5, fontSize: 12 },
                '& .MuiStepIcon-root': { fontSize: 22 },
              }}>
                {mobileStepLabels.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
              </Stepper>
            </Paper>

            {mobileSaleStep === 0 && <>
              <CustomerSelector />
              <Button
                fullWidth
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                disabled={!state.selectedCustomer}
                onClick={() => setMobileSaleStep(1)}
                sx={{ minHeight: 48 }}
              >
                {t('sales.continueToItems')}
              </Button>
            </>}

            {mobileSaleStep === 1 && <>
              <ItemSelector batchTrackingEnabled={user?.batchTrackingEnabled} />
              <Stack direction="row" spacing={1}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  startIcon={<ArrowBack />}
                  onClick={() => setMobileSaleStep(0)}
                  sx={{ minHeight: 48 }}
                >
                  {t('sales.backToCustomer')}
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  disabled={state.cartItems.length === 0}
                  onClick={() => setMobileSaleStep(2)}
                  sx={{ minHeight: 48 }}
                >
                  {t('sales.reviewCart')}{cartItemCount > 0 ? ` (${cartItemCount})` : ''}
                </Button>
              </Stack>
            </>}

            {mobileSaleStep === 2 && <>
              <Button
                variant="text"
                startIcon={<ArrowBack />}
                onClick={() => setMobileSaleStep(1)}
                sx={{ alignSelf: 'flex-start', minHeight: 44, px: 0 }}
              >
                {t('sales.backToItems')}
              </Button>
              <ShoppingCart onSaleCompleted={handleSaleCompleted} />
            </>}
          </Stack>
        ) : (
          <SalesShoppingPanel
            key={shoppingPanelKey}
            onSaleCompleted={handleSaleCompleted}
            onItemAdded={() => undefined}
            onCustomerSelected={() => undefined}
            batchTrackingEnabled={user?.batchTrackingEnabled}
          />
        )}
      </TabPanel>}
      <TabPanel value={activeTab} index={1}>
        <SalesHistoryPanel enabled={activeTab === 1} companyId={companyId} />
      </TabPanel>

      {cartItemCount > 0 && activeTab === 1 && (
        <Fab
          color="primary"
          aria-label={t('sales.shoppingCart')}
          sx={{ position: 'fixed', bottom: { xs: 'calc(76px + env(safe-area-inset-bottom))', md: 16 }, right: 16, display: { xs: 'flex', lg: 'none' } }}
          onClick={() => { setActiveTab(0); setMobileSaleStep(2); }}
        >
          <Badge badgeContent={cartItemCount} color="secondary"><CartIcon /></Badge>
        </Fab>
      )}

      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={6000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowSuccessMessage(false)} severity="success" sx={{ width: '100%' }}>
          {t('sales.saleCompletedSuccessfully')}
        </Alert>
      </Snackbar>

      <SaleSummaryDialog
        open={completedSale !== null}
        sale={completedSale}
        onClose={handleSaleSummaryClose}
      />
    </Box>
  );
};

export function SalesPage() {
  return (
    <ShoppingCartProvider>
      <SalesPageContent />
    </ShoppingCartProvider>
  );
}
